import { useEffect, useRef } from "react";
import { ScrollView, YStack } from "tamagui";

import {
  AccessEnrollmentProgressionRequestFlowName,
  DefaultVisitMetadataVisitTypePreference,
  type UserProgressionStateModel,
  VisitType,
} from "@/api/generated/models";
import { Heading, Paragraph } from "@/components";
import Ellipses from "@/components/animated/Ellipses";
import { useUserProgression } from "@/conversation/sidebar/UserProgression/UserProgressionContext";
import VisitBlockedByMergeNotice from "@/conversation/sidebar/VisitBlockedByMergeNotice";
import { useCuraiVisitRedirect } from "@/hooks/useCuraiVisitRedirect";

const ACCESS_ENROLLMENT_FLOWS = new Set<string>(
  Object.values(AccessEnrollmentProgressionRequestFlowName),
);

type ClinicRedirectProps = {
  onClose?: () => void;
  conversationId?: string; // required to start the visit
  clinicRequestId?: string; // required to start the visit
  originClinicRequestId?: string; // required to start the visit
  onContinue?: () => void;
  // Render the selection step the backend reset to after refusing the visit (402/409).
  onVisitBlocked?: (progression: UserProgressionStateModel) => void;
};

const ClinicRedirect = ({
  onClose,
  conversationId,
  clinicRequestId,
  originClinicRequestId,
  onContinue,
  onVisitBlocked,
}: ClinicRedirectProps) => {
  const userProgressionContext = useUserProgression();
  const metadata = userProgressionContext?.progression.metadata;
  const visitTypePreference = metadata?.visit_type_preference as
    | DefaultVisitMetadataVisitTypePreference
    | undefined;
  // The backend resolves is_av_call_required during state_validation on_complete.
  const avCallRequired = metadata?.is_av_call_required as boolean | undefined;
  const isAccessEnrollment = ACCESS_ENROLLMENT_FLOWS.has(
    userProgressionContext?.progression.flow_name ?? "",
  );
  // When set, the paid modality drives the encounter type; preference/avCallRequired only drive the
  // no-selection free flow.
  const paymentVisitType = isAccessEnrollment
    ? VisitType.live_video
    : (metadata?.payment_visit_type as VisitType | undefined);

  const { startVisit, isLoading, isBlockedByMerge } = useCuraiVisitRedirect({
    conversationId: conversationId ?? "",
    clinicRequestId,
    originClinicRequestId,
    userProgressionId: userProgressionContext?.progression.id,
    visitTypePreference,
    avCallRequired,
    paymentVisitType,
    onVisitStarted: () => {
      onContinue?.();
    },
    onVisitBlocked,
  });
  const hasStartedRef = useRef(false);

  useEffect(() => {
    // Never auto-start a visit while an account merge is in progress.
    if (isLoading || isBlockedByMerge || hasStartedRef.current) return;
    hasStartedRef.current = true;
    startVisit();
  }, [isLoading, isBlockedByMerge, startVisit]);

  if (isBlockedByMerge) {
    return <VisitBlockedByMergeNotice />;
  }

  return (
    <ScrollView
      flex={1}
      contentContainerStyle={{
        flex: 1,
        paddingHorizontal: "$200",
        paddingBottom: "$400",
      }}
    >
      <YStack flex={1} gap="$150">
        <Heading size="$lg">
          Starting your visit
          <Ellipses />
        </Heading>
        <Paragraph size="$sm" color="$textSecondary">
          Please wait while we connect you with a clinician.
        </Paragraph>
      </YStack>
    </ScrollView>
  );
};

export default ClinicRedirect;
